import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, api, keys } from "@shared/api";
import { stamp, type StampStyle } from "@shared/lib/format";
import { parse, plainText, type Block, type Inline } from "@shared/lib/markup";
import { IconChevronRight, IconTextChannel, IconThreads } from "@shared/ui/icons";
import { Dialog } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { splitEmoji, type CustomEmoji } from "@shared/lib/emoji";
import { FileImage } from "@shared/ui/FileImage";
import { readMessageLink, type MessageLink } from "@shared/lib/jump";

/**
 * Показ размеченного текста.
 *
 * Разбор живёт отдельно и ничего не знает про React: он же нужен для
 * оповещений и для проверки «названы ли мы», а там разметка не рисуется.
 */

/**
 * Где читают ссылку: какой сервер открыт и как выглядит чужой. Серверы знает
 * верхний слой, он и даёт это обёрткой. Без неё ссылка рисуется как ссылка
 * с этого же сервера.
 */
export interface LinkPlaces {
  /** Открытый сервер; `null` — личные сообщения. */
  here: string | null;
  /** Значок и имя сервера, на который ведёт ссылка. `null` — человек не на нём. */
  serverOf: (id: string) => { name: string; tile: ReactNode } | null;
}

const Places = createContext<LinkPlaces | null>(null);

export function LinkPlacesProvider({ value, children }: { value: LinkPlaces; children: ReactNode }) {
  return <Places.Provider value={value}>{children}</Places.Provider>;
}

/** Что показу нужно знать про имена и знаки — одним свёртком на все уровни. */
interface Look {
  /** Имя по хэндлу: упоминание показывается именем, а хранится хэндлом. */
  nameOf: (handle: string) => string | null;
  /** Свой хэндл — по нему видно, что назвали именно нас. */
  mine: string;
  /** Знак сервера по имени. `null` — такого здесь нет. */
  emojiOf: (name: string) => CustomEmoji | null;
  /** Имя канала по номеру. `null` — канала нет или он не виден. */
  channelOf?: (id: string) => string | null;
  /** Имя роли по номеру. `null` — роли уже нет. */
  roleOf?: (id: string) => string | null;
  /** Открыть канал по нажатию на упоминание. */
  onOpenChannel?: (id: string) => void;
  /** Перейти к сообщению по ссылке на него. Без него ссылка — обычная. */
  onOpenMessage?: (link: MessageLink) => void;
}

export function Markup({
  text,
  nameOf,
  mine,
  emojiOf,
  channelOf,
  roleOf,
  onOpenChannel,
  onOpenMessage,
}: {
  text: string;
  nameOf: (handle: string) => string | null;
  mine: string;
  emojiOf?: (name: string) => CustomEmoji | null;
  /** Имя канала по номеру. Нет канала — упоминание останется текстом. */
  channelOf?: (id: string) => string | null;
  /** Имя роли по номеру. Нет роли — упоминание останется текстом. */
  roleOf?: (id: string) => string | null;
  /** Открыть канал. Без него упоминание рисуется, но не нажимается. */
  onOpenChannel?: (id: string) => void;
  /** Перейти к сообщению по ссылке на него. Без него ссылка открывается как обычная. */
  onOpenMessage?: (link: MessageLink) => void;
}) {
  const look: Look = {
    nameOf,
    mine,
    emojiOf: emojiOf ?? (() => null),
    channelOf,
    roleOf,
    onOpenChannel,
    onOpenMessage,
  };

  // Пустые строки в конце не рисуются: абзац ниже дорисовывает свою последнюю
  // пустую строку, и под сообщением бота с хвостом переводов выросло бы место.
  // Пустой текст по той же причине — ничто, а не пустая строка.
  if (!text.trim()) return null;

  return (
    <>
      {parse(text.trimEnd()).map((block, index) => (
        <BlockView key={index} block={block} look={look} />
      ))}
    </>
  );
}

function BlockView({ block, look }: { block: Block; look: Look }) {
  switch (block.kind) {
    case "code":
      return (
        <div className="mk-code">
          {/*
            Язык подписан, но не подсвечен: подсветка — это разборщик на
            каждый язык, и брать его ради редкой вставки в переписке рано.
            Подпись при этом честна: по ней видно, чего ждать от блока.
          */}
          {block.lang && <span className="mk-code-lang">{block.lang}</span>}
          <pre>{block.text}</pre>
        </div>
      );

    case "quote":
      return (
        <blockquote className="mk-quote">
          {block.blocks.map((inner, index) => (
            <BlockView key={index} block={inner} look={look} />
          ))}
        </blockquote>
      );

    case "list": {
      const items = block.items.map((item, index) => (
        <li key={index}>
          {item.map((inner, at) => (
            <BlockView key={at} block={inner} look={look} />
          ))}
        </li>
      ));

      if (!block.ordered) return <ul className="mk-list">{items}</ul>;

      // Место под номер — по самому длинному, как в Discord: «1000.» иначе
      // вылезло бы за край слева.
      const digits = String(block.start + block.items.length - 1).length;

      return (
        <ol className="mk-list" start={block.start} style={{ "--mk-digits": digits } as React.CSSProperties}>
          {items}
        </ol>
      );
    }

    case "heading":
      return (
        <span className={`mk-heading mk-h${block.level}`} role="heading" aria-level={block.level}>
          <InlineView parts={block.parts} look={look} />
        </span>
      );

    case "subtext":
      return (
        <span className="mk-subtext">
          <InlineView parts={block.parts} look={look} />
        </span>
      );

    default:
      return (
        <span className="mk-line">
          <InlineView parts={block.parts} look={look} />
          {/*
            Пустая строка в конце абзаца — тоже строка. Последний перевод
            `pre-wrap` не рисует, и пустая строка перед списком пропадала:
            два списка через пустую строку сливались в один.
          */}
          {endsBlank(block.parts) && "\n"}
        </span>
      );
  }
}

function endsBlank(parts: Inline[]): boolean {
  const last = parts.at(-1);

  return !last || (last.kind === "text" && last.text.endsWith("\n"));
}

function InlineView({ parts, look }: { parts: Inline[]; look: Look }): ReactNode {
  return (
    <>
      {parts.map((part, index) => {
        switch (part.kind) {
          case "text":
            return <PlainText key={index} text={part.text} />;

          case "code":
            /*
             * Внутри кода знаки не выделяются: там показывают исходник, и
             * поднятый до восемнадцати пунктов смайлик разъехался бы с
             * моноширинными буквами вокруг.
             */
            return (
              <code key={index} className="mk-inline-code">
                {part.text}
              </code>
            );

          case "strong":
            return (
              <strong key={index}>
                <InlineView parts={part.parts} look={look} />
              </strong>
            );

          case "em":
            return (
              <em key={index}>
                <InlineView parts={part.parts} look={look} />
              </em>
            );

          case "underline":
            return (
              <u key={index}>
                <InlineView parts={part.parts} look={look} />
              </u>
            );

          case "strike":
            return (
              <s key={index}>
                <InlineView parts={part.parts} look={look} />
              </s>
            );

          case "spoiler":
            return (
              <Spoiler key={index}>
                <InlineView parts={part.parts} look={look} />
              </Spoiler>
            );

          case "mention": {
            const known = look.nameOf(part.handle);

            // Неизвестный хэндл остаётся текстом: подсвечивать имя, за
            // которым никого нет, значит обещать человека, которого не
            // позвали.
            if (!known) return `@${part.handle}`;

            return (
              <span key={index} className={`mention ${part.handle === look.mine ? "me" : ""}`}>
                @{known}
              </span>
            );
          }

          case "channel": {
            const known = look.channelOf?.(part.id) ?? null;

            /*
             * Канала нет или он не виден — остаётся текстом, по тому же
             * правилу, что и неизвестный хэндл: подсвечивать ссылку, которая
             * никуда не ведёт, значит обещать несуществующее. Так же выглядит
             * упоминание чужого канала, пересланное сюда.
             */
            if (!known) return "#канал";

            return (
              <button
                key={index}
                type="button"
                className="mention channel"
                onClick={() => look.onOpenChannel?.(part.id)}
              >
                #{known}
              </button>
            );
          }

          case "everyone":
            return (
              <span key={index} className="mention role">
                @{part.word}
              </span>
            );

          case "role": {
            const known = look.roleOf?.(part.id) ?? null;

            /*
             * Роли уже нет — остаётся текстом, по тому же правилу, что и
             * неизвестный хэндл: показывать роль, которой не существует,
             * значит обещать несуществующее.
             *
             * Нажимать не на что намеренно: у роли нет своего экрана, а
             * список её людей лежит в настройках сервера, куда пускают не
             * всех.
             */
            if (!known) return "@роль";

            return (
              <span key={index} className="mention role">
                @{known}
              </span>
            );
          }

          case "emoji":
            return <CustomMark key={index} name={part.name} emojiOf={look.emojiOf} />;

          case "time":
            return <Stamp key={index} at={part.at} style={part.style} />;

          case "link": {
            /*
             * Ссылка на сообщение здесь — плашкой «#канал › сообщение», как в
             * Discord, и переходом внутри приложения, а не новой вкладкой.
             */
            const target = look.onOpenMessage ? readMessageLink(part.href) : null;
            if (target?.threadId) {
              return (
                <ThreadLink
                  key={index}
                  serverId={target.serverId}
                  channelId={target.channelId}
                  threadId={target.threadId}
                  href={part.href}
                  onOpen={() => look.onOpenMessage?.(target)}
                />
              );
            }
            if (target) {
              return (
                <button
                  key={index}
                  type="button"
                  className="mention channel"
                  title={part.href}
                  onClick={() => look.onOpenMessage?.(target)}
                >
                  #{look.channelOf?.(target.channelId) ?? "канал"}
                  {target.messageId && " › сообщение"}
                </button>
              );
            }

            return (
              <a
                key={index}
                className="mk-link"
                href={part.href}
                target="_blank"
                // Без этого открытая страница получает ссылку на наше окно.
                rel="noreferrer noopener"
                // У подписанной ссылки адрес не виден — показываем его по наведению.
                title={part.label ? part.href : undefined}
              >
                {part.label ? <InlineView parts={part.label} look={look} /> : part.href}
              </a>
            );
          }
        }
      })}
    </>
  );
}

/**
 * Ссылка на ветку — плашкой с её значком и именем, как в Discord, а не
 * «#канал». Имени в ссылке нет, его спрашивают у сервера — раз на ветку,
 * ответ кэшируется. Ветка с другого сервера начинается его значком и именем:
 * «Сервер › ветка».
 *
 * Ветки нет или в неё нельзя (сервер ответил 403 или 404) — как в Discord,
 * «# неизвестно»: ни имени ветки, ни сервера. Щелчок по ней не ведёт никуда,
 * а говорит, что доступа нет.
 */
function ThreadLink({
  serverId,
  channelId,
  threadId,
  href,
  onOpen,
}: {
  serverId: string | null;
  channelId: string;
  threadId: string;
  href: string;
  onOpen: () => void;
}) {
  const places = useContext(Places);
  const [refused, setRefused] = useState(false);
  const { data, error } = useQuery({
    queryKey: keys.thread(threadId),
    queryFn: () => api.threads.get(channelId, threadId),
    staleTime: 60_000,
    retry: false,
  });

  if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
    return (
      <>
        <button type="button" className="mention channel unknown" onClick={() => setRefused(true)}>
          <IconTextChannel size={13} />
          <i>неизвестно</i>
        </button>
        {refused && <NoAccess onClose={() => setRefused(false)} />}
      </>
    );
  }

  const name = data
    ? data.title || plainText(data.root?.content.split("\n")[0] ?? "").slice(0, 80) || "Ветка"
    : "ветка";
  const away = places && serverId && serverId !== places.here ? places.serverOf(serverId) : null;

  return (
    <button type="button" className="mention channel thread" title={href} onClick={onOpen}>
      {away && (
        <span className="mention-away">
          {away.tile}
          {away.name}
          <IconChevronRight size={12} />
        </span>
      )}
      <IconThreads size={13} />
      {name}
    </button>
  );
}

/** «У вас нет доступа к этой ссылке» — слова Discord. */
function NoAccess({ onClose }: { onClose: () => void }) {
  return (
    <Dialog
      title="У вас нет доступа к этой ссылке"
      onClose={onClose}
      actions={
        <Button type="button" onClick={onClose}>
          ОК
        </Button>
      }
    >
      <p className="small">Это ссылка на сервер или канал, к которому у вас нет доступа.</p>
    </Dialog>
  );
}

/**
 * Обычный текст со знаками внутри.
 *
 * Знак набирается своим кеглем — 18 пунктов при тексте в 14, — но строка от
 * этого не растёт: межстрочный интервал держится обнулённой высотой строки
 * самого знака, а на место его сажает сдвиг базовой линии. Крупнее — строки
 * разъедутся, ровнее — знак потеряется в буквах.
 */
function PlainText({ text }: { text: string }) {
  const runs = splitEmoji(text);

  // Знаков нет — и оборачивать нечего: в переписке так с большинством строк.
  if (runs.length === 1 && !runs[0].emoji) return text;

  return (
    <>
      {runs.map((run, index) =>
        run.emoji ? (
          <span key={index} className="emoji-mark">
            {run.text}
          </span>
        ) : (
          run.text
        ),
      )}
    </>
  );
}

/**
 * Кастомный знак сервера.
 *
 * Чужой знак не подменяется картинкой-заглушкой и не исчезает: остаётся его
 * имя в рамке. Заведёте такой же у себя — строка сама станет знаком, и
 * старые сообщения покажут его задним числом.
 */
function CustomMark({
  name,
  emojiOf,
}: {
  name: string;
  emojiOf: (name: string) => CustomEmoji | null;
}) {
  const known = emojiOf(name);

  if (!known) {
    return (
      <span className="emoji-absent" title={`:${name}: — такого знака здесь нет`}>
        :{name}:
      </span>
    );
  }

  return (
    <FileImage id={known.file_id} alt={`:${name}:`} className="emoji-mark-image" />
  );
}

/**
 * Метка времени: дата в поясе читающего, полная — в подсказке. «Через 2 часа»
 * пересчитывается раз в полминуты, остальные виды не меняются.
 */
function Stamp({ at, style }: { at: Date; style: StampStyle }) {
  const [now, setNow] = useState(() => Date.now());

  /*
   * Относительная метка тикает, как в Discord: последняя минута — каждую
   * секунду («через 59 секунд» → «через 58»), последний час — каждые 10
   * секунд, дальше — раз в минуту. Пересчёт сам назначает следующий: чем
   * ближе срок, тем чаще. Остальные виды — дата и время — от «сейчас» не
   * зависят, им тикать незачем.
   */
  useEffect(() => {
    if (style !== "R") return;
    const left = Math.abs(at.getTime() - now);
    const delay = left < 60_000 ? 1_000 : left < 3_600_000 ? 10_000 : 60_000;
    const timer = window.setTimeout(() => setNow(Date.now()), delay);
    return () => window.clearTimeout(timer);
  }, [style, at, now]);

  return (
    <time className="mk-time" dateTime={at.toISOString()} title={stamp(at, "F")}>
      {stamp(at, style, now)}
    </time>
  );
}

/**
 * Спойлер: закрыт, пока не нажали.
 *
 * Раскрывается насовсем, а не пока держишь: прочитать спрятанное с одной руки
 * должно быть можно, и закрывать его обратно незачем — секрет уже показан.
 */
function Spoiler({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <span
      className={`mk-spoiler ${open ? "open" : ""}`}
      role="button"
      tabIndex={open ? -1 : 0}
      title={open ? undefined : "Показать скрытое"}
      onClick={() => setOpen(true)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setOpen(true);
        }
      }}
    >
      {children}
    </span>
  );
}
